import { useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { usePriceBooks, useTerritories } from "@/api/commerce";
import { useSaveAccount } from "@/api/crm";
import { useMe } from "@/api/hooks";
import { ACCOUNT_TYPES, type Account, type CustomValues } from "@/api/types";
import { CustomFieldsFieldset } from "@/components/crm/CustomFieldsFieldset";
import { LocationFields } from "@/components/crm/LocationFields";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, TextField } from "@/components/ui";
import { titleCase } from "@/lib/format";
import { can } from "@/lib/permissions";

const TEXT = ["name", "industry", "website", "phone", "email", "tax_id", "address_line", "city", "state", "country", "postal_code", "customer_group", "ledger_name"] as const;
type Values = Record<(typeof TEXT)[number], string> & { account_type: string };

export function AccountForm({ account, onSaved, onCancel }: { account?: Account; onSaved: (a: Account) => void; onCancel?: () => void }) {
  const save = useSaveAccount(account?.id);
  const perms = useMe().data?.permissions ?? [];
  const books = usePriceBooks();
  const territories = useTerritories();
  const [priceBook, setPriceBook] = useState(account?.price_book?.id ?? "");
  const initialTerritory = account?.territory?.id ?? "auto";
  const [territory, setTerritory] = useState(initialTerritory);
  const [creditLimit, setCreditLimit] = useState(account?.credit_limit != null ? String(account.credit_limit) : "");
  const [custom, setCustom] = useState<CustomValues>(account?.custom ?? {});
  const [parent, setParent] = useState<PickerValue | null>(account?.parent ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<Values>({
    defaultValues: { ...Object.fromEntries(TEXT.map((k) => [k, account?.[k] ?? ""])), account_type: account?.account_type ?? "prospect" } as Values,
  });
  const { register, handleSubmit } = form;

  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setFormError(null);
    const body: Record<string, unknown> = { account_type: v.account_type, custom, parent_id: parent?.id ?? null, price_book_id: priceBook || null, credit_limit: creditLimit.trim() === "" ? null : creditLimit.trim() };
    for (const k of TEXT) body[k] = v[k].trim() === "" ? null : v[k].trim();
    // Territory follows the state unless someone picks one.
    if (territory !== initialTerritory) body.territory_id = territory === "auto" ? null : territory;
    if (!body.name) { setErrors({ name: "Name is required" }); return; }
    try {
      onSaved(await save.mutateAsync(body));
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else if (e instanceof ApiError && e.status === 409) setErrors({ name: e.message });
      else setFormError(e instanceof Error ? e.message : "Could not save");
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-6" noValidate>
      {formError && <Alert>{formError}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField label="Account name *" {...register("name")} error={errors.name} />
        <div>
          <label htmlFor="account_type" className="label">Type</label>
          <select id="account_type" className="input" {...register("account_type")}>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
          </select>
        </div>
        <TextField label="Industry" {...register("industry")} error={errors.industry} />
        <TextField label="Website" placeholder="example.com" {...register("website")} error={errors.website} />
        <TextField label="Phone" {...register("phone")} error={errors.phone} />
        <TextField label="Email" type="email" {...register("email")} error={errors.email} />
        <RecordPicker label="Parent account" endpoint="/accounts" value={parent} onChange={setParent} error={errors.parent_id} />
        <div className="sm:col-span-2"><TextField label="Address" {...register("address_line")} error={errors.address_line} /></div>
        <TextField label="City" {...register("city")} error={errors.city} />
        <TextField label="PIN / Postal code" {...register("postal_code")} error={errors.postal_code} />
        <LocationFields form={form} errors={errors} taxHint="Decides how quotes are taxed: e.g. a valid EU VAT number means reverse charge" />
      </div>
      {can(perms, "products:read") && (
        <fieldset className="grid gap-4 rounded-md border border-line p-4 sm:grid-cols-2">
          <legend className="px-1 text-sm font-semibold">Pricing, territory and accounts</legend>
          <TextField label="Customer group" placeholder="e.g. OEM, Distributor" hint="Price books for this group apply" {...register("customer_group")} error={errors.customer_group} />
          <div>
            <label htmlFor="price_book" className="label">Price book</label>
            <select id="price_book" className="input" value={priceBook} onChange={(e) => setPriceBook(e.target.value)}>
              <option value="">Best match for group and region</option>
              {books.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {errors.price_book_id && <p className="field-error">{errors.price_book_id}</p>}
          </div>
          <div>
            <label htmlFor="territory" className="label">Territory</label>
            <select id="territory" className="input" value={territory} onChange={(e) => setTerritory(e.target.value)}>
              <option value="auto">Automatic (from the state)</option>
              {territories.data?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <TextField id="credit_limit" label="Credit limit" inputMode="decimal" placeholder="No limit" hint="Orders that would take the unpaid total past this wait for a manager" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} error={errors.credit_limit} />
          <TextField label="Name in Tally / Zoho Books" placeholder="Same as the account name" hint="The customer's ledger name, if different" {...register("ledger_name")} error={errors.ledger_name} />
        </fieldset>
      )}
      <CustomFieldsFieldset objectType="account" value={custom} onChange={setCustom} errors={errors} />
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn-primary" disabled={save.isPending}>{save.isPending ? "Saving…" : account ? "Save changes" : "Create account"}</button>
      </div>
    </form>
  );
}
